import { addError } from "@wayflyer/blackbox-fe";
import { Callout, UploadArea, FilesList, Flex, Form, Text, formatDisplayDate } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import * as React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { getBankAccountMasksAsString, getEndDateForPeriodText, useErrorModal } from "shared-ui";

import { useAirReconnectConnectionMutation } from "../../../../queries/air-response/air-reconnect-connection.mutations";
import { useBankStatementSubmitMutation } from "../../../../queries/bank-connect/bank-statement.mutations";
import { COMMON_MESSAGES } from "../../../common-messages";
import { ConfirmBankStatementFields } from "../../components";
import { useBankStatementUploadManager } from "../../hooks/bank-statement-upload-manager";
import { AIR_MESSAGES } from "../../message-definitions";
import type { BankStatementUploadsFormFields } from "../../types/bank-statements";
import { getAcceptedFileTypes, getStatementTypeMessage } from "../../utils";

import { MESSAGES } from "./message-definitions";

dayjs.extend(LocalizedFormat);
dayjs.extend(utc);

export interface BankStatementUploadProps {
  airPublicId: string;
  bankName?: string | null;
  bankAccountMasks?: string[];
  customerDomain: string;
  onBack: () => void;
  onSubmit: () => void;
}

/**
 * Reconnect connection sad path for reconnecting bank
 */
export const BankStatementUpload = ({
  airPublicId,
  bankName,
  bankAccountMasks,
  customerDomain,
  onBack,
  onSubmit: onSuccess,
}: BankStatementUploadProps): UIElement => {
  const intl = useIntl();

  const [unableToProvideStatements, setUnableToProvideStatements] = useState<boolean | null>(null);
  const [unableToSendStatmentsReasonsValid, setUnableToSendStatmentsReasonsValid] = useState<boolean>(false);

  const submitBankStatements = useBankStatementSubmitMutation(customerDomain);
  const updateReconnectConnectionAIR = useAirReconnectConnectionMutation(customerDomain);

  const { openErrorModal } = useErrorModal();
  const logErrorHandler = React.useCallback(
    (e: unknown) => {
      if (e instanceof Error) {
        openErrorModal({ variant: "submit-error", error: e });
      }
    },
    [openErrorModal]
  );

  const useUploadManager = useBankStatementUploadManager(customerDomain);
  const fileUploadProps = useUploadManager();

  const sixMonthperiod: FundingApplicationSchemas.DateTimeRangeSchema = {
    start: dayjs().subtract(6, "months").startOf("month").toISOString(),
    end: dayjs().toISOString(),
  };

  const form = useForm<BankStatementUploadsFormFields>({
    defaultValues: {
      file_uploads: [],
      unable_to_provide_statements_reason: null,
      unable_to_provide_statements_reason_other: null,
      unable_to_provide_statements_reason_open_date: null,
      unable_to_provide_statements_reason_close_dates: null,
    },
  });

  const [formError, setFormError] = useState<string | undefined>();

  const periodString = intl.formatMessage(COMMON_MESSAGES.periodText, {
    dateFrom: formatDisplayDate(dayjs(sixMonthperiod.start).startOf("month")),
    dateTo: formatDisplayDate(getEndDateForPeriodText(sixMonthperiod.end)),
  });

  React.useEffect(() => {
    form.setValue(
      "file_uploads",
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      fileUploadProps.uploadsState.uploads
        .filter((uploadState) => uploadState.status === "complete" && uploadState.uploadedFile)
        .map((uploadState) => uploadState.uploadedFile)
    );
  }, [fileUploadProps.uploadsState, form]);

  const postAirUpdate = (objectKeys: string[]) => {
    const cannotReconnectConnectionReason: FundingApplicationSchemas.CannotReconnectConnectionsReasonCode | null =
      (() => {
        if (unableToSendStatmentsReasonsValid) {
          // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
          if (form.watch("unable_to_provide_statements_reason") === "other") {
            return "other";
          }
          if (form.watch("unable_to_provide_statements_reason") === "closed") {
            return "bank_accounts_closed";
          }
        }
        if (objectKeys.length) {
          return "bank_statements_provided";
        }
        return null;
      })();

    updateReconnectConnectionAIR
      .mutateAsync({
        airPublicId,
        objectKeys,
        cannotReconnectConnectionReason,
        reason: unableToSendStatmentsReasonsValid ? form.watch("unable_to_provide_statements_reason_other") : null,
        bankAccountCloseDates: unableToSendStatmentsReasonsValid
          ? form
              .watch("unable_to_provide_statements_reason_close_dates")
              ?.filter(({ closeDate }) => closeDate !== null)
              .map(({ accountMask, closeDate }) => ({
                account_mask: accountMask,
                close_date: closeDate as string,
              }))
          : null,
      })
      .then(() => {
        onSuccess();
      })
      .catch((e) => {
        logErrorHandler(e);
      });
  };

  const submitClickHandler = (formData: BankStatementUploadsFormFields) => {
    const objectKeys = formData.file_uploads.map((file) => file.objectKey);
    if (objectKeys.length) {
      submitBankStatements
        .mutateAsync({
          object_keys: objectKeys,
          group: bankName ? bankName : `Account(s) ${getBankAccountMasksAsString(intl, bankAccountMasks ?? [])}`,
        })
        .then(() => {
          postAirUpdate(objectKeys);
        })
        .catch((e) => {
          logErrorHandler(e);
        });
    } else if (unableToSendStatmentsReasonsValid) {
      postAirUpdate(objectKeys);
    }
  };

  const onUploadsComplete = (formData: BankStatementUploadsFormFields) => {
    try {
      submitClickHandler(formData);
    } catch (e: any) {
      setFormError(e.message);
    }
  };

  const bannerText = (() => {
    if ((bankAccountMasks?.length ?? 0) === 0) {
      return intl.formatMessage(
        {
          id: "funding-application: air reconnect bank statement required plus period description  (no mask)",
          defaultMessage: "Please upload statements for your {bankName} bank for the period {period}",
        },
        {
          bankName: bankName ? bankName : "",
          period: <strong>{periodString}</strong>,
        }
      );
    }
    return intl.formatMessage(
      {
        id: "funding-application: air reconnect bank statement required accounts plus period description (with masks)",
        defaultMessage:
          "Please upload statements for your {bankName} {noBankAccountMasks, plural, =1 {account} other {accounts}} ending in {bankAccountMasks} for the period {period}",
      },
      {
        p: (chunks: any) => <Text>{chunks}</Text>,
        bankName: bankName ? bankName : intl.formatMessage(AIR_MESSAGES.bankStatementDefaultBankName),
        noBankAccountMasks: bankAccountMasks?.length,
        bankAccountMasks: getBankAccountMasksAsString(intl, bankAccountMasks ?? []),
        period: <strong>{periodString}</strong>,
      }
    );
  })();

  return (
    <Flex gap="4" direction="column">
      <Flex direction="column" gap="6">
        <Flex direction="column" gap="8">
          <Text>{intl.formatMessage(MESSAGES.cantConnectBankStatementDetailsText)}</Text>
          <Callout variant="info" title={bannerText} />
        </Flex>
        <Form
          primaryAction={{
            content: intl.formatMessage(AIR_MESSAGES.bankStatementCompleteRequestButtonText),
            analyticsId: "reconnect-bank-statement-upload-submit",
            disabled:
              (!fileUploadProps.uploadsState?.uploads.length ||
                fileUploadProps.uploadsState?.uploadsComplete === false ||
                !!formError ||
                unableToProvideStatements !== false) &&
              !unableToSendStatmentsReasonsValid,
          }}
          secondaryAction={{
            content: intl.formatMessage(COMMON_MESSAGES.backLabel),
            analyticsId: "reconnect-bank-statement-upload-back",
            onAction: onBack,
          }}
          onSubmit={form.handleSubmit(onUploadsComplete, (e) => addError(e, { message: "form error" }))}
        >
          {formError && <Callout variant="critical" description={formError} />}
          <UploadArea
            label={intl.formatMessage(AIR_MESSAGES.bankStatementBankStatementsText)}
            testId="reconnect-bank-statements-uploader"
            onFilesAdded={fileUploadProps.onFilesAdded}
            acceptedFileTypes={getAcceptedFileTypes([sixMonthperiod])}
            sizeLimitMB={fileUploadProps.sizeLimitMB}
            multiple
          />
          {fileUploadProps.uploadsState && fileUploadProps.uploadsState.uploads.length > 0 ? (
            <FilesList uploadsState={fileUploadProps.uploadsState} onFileRemoved={fileUploadProps.onFileRemoved} />
          ) : null}
          <Text>{getStatementTypeMessage(intl, [sixMonthperiod])}</Text>
          <ConfirmBankStatementFields
            form={form}
            bankName={bankName}
            airType="reconnect_connection"
            bankAccountMasks={bankAccountMasks}
            unableToProvideStatements={unableToProvideStatements}
            setUnableToProvideStatements={setUnableToProvideStatements}
            setUnableToSendStatmentsReasonsValid={setUnableToSendStatmentsReasonsValid}
          />
        </Form>
      </Flex>
    </Flex>
  );
};
